import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingService, type Subscription } from '../services/billing.service';
import { TIERS, prixAffiche } from './CapabilityGate';
import { getErrorMessage } from '../utils/error';
import './SubscriptionSection.css';

/**
 * Encart « Abonnement » de l'écran Mon compte : ce que le bailleur paie, quand
 * il sera reconduit, et comment arrêter.
 *
 * La résiliation ne coupe rien tout de suite : Stripe laisse l'abonnement
 * courir jusqu'à l'échéance déjà réglée. C'est pourquoi la confirmation annonce
 * la DATE exacte de fin d'accès et la dépublication du site — un « êtes-vous
 * sûr ? » générique laisserait croire à une coupure immédiate.
 */
const formaterDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : '—';

const SubscriptionSection: React.FC = () => {
  const navigate = useNavigate();
  const [abo, setAbo] = useState<Subscription | null>(null);
  const [charge, setCharge] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const recharger = () =>
    billingService
      .getSubscription()
      .then(setAbo)
      .catch(() => setAbo(null))
      .finally(() => setCharge(true));

  useEffect(() => {
    recharger();
  }, []);

  if (!charge) return null;

  const palier = TIERS.find((t) => t.tier === abo?.tier);
  const actif = abo?.status === 'active' && !!abo?.tier;

  const agir = async (action: 'cancel' | 'resume') => {
    setBusy(true);
    setErreur(null);
    try {
      if (action === 'cancel') await billingService.cancelSubscription();
      else await billingService.resumeSubscription();
      await recharger();
      setConfirmation(false);
    } catch (e) {
      setErreur(getErrorMessage(e, 'Action impossible pour le moment.'));
    } finally {
      setBusy(false);
    }
  };

  // Le portail Stripe porte le changement d'offre, la carte et les factures.
  // `busy` reste levé en cas de succès : la page s'en va.
  const gerer = async () => {
    setBusy(true);
    setErreur(null);
    try {
      window.location.href = await billingService.createPortalSession();
    } catch (e) {
      setErreur(getErrorMessage(e, 'Accès à la gestion de votre abonnement impossible pour le moment.'));
      setBusy(false);
    }
  };

  // Paiement échoué : l'abonnement existe toujours chez Stripe. Proposer de
  // payer à nouveau en créerait un second ; le portail permet de changer de carte.
  if (abo?.status === 'past_due') {
    return (
      <div className="account-section">
        <h3>Abonnement</h3>
        <p className="subscription-canceled">
          Votre dernier paiement a échoué. Mettez à jour votre carte bancaire
          pour régler votre abonnement.
        </p>
        <button className="subscription-cta" onClick={gerer} disabled={busy}>
          Gérer mon abonnement
        </button>
        {erreur && <p className="subscription-error">{erreur}</p>}
      </div>
    );
  }

  // Aucun abonnement : on renvoie vers l'offre plutôt que d'afficher un encart
  // vide qui n'apprendrait rien.
  if (!actif) {
    return (
      <div className="account-section">
        <h3>Abonnement</h3>
        <p className="subscription-empty">
          Vous n&apos;avez pas d&apos;abonnement en cours. La publication de
          votre site public nécessite un plan payant.
        </p>
        <button className="subscription-cta" onClick={() => navigate('/upgrade')}>
          Voir les offres
        </button>
      </div>
    );
  }

  const resilie = abo!.cancelAtPeriodEnd;
  const essai = !!abo!.trialEnd;

  return (
    <div className="account-section">
      <h3>Abonnement</h3>

      <div className="account-info">
        <div className="info-row">
          <span className="info-label">Formule</span>
          {/* Le prix dépend de la périodicité : sans elle on n'en affiche
              aucun, plutôt que d'annoncer le mensuel à un client facturé à
              l'année. `interval` est nul pour les abonnements souscrits avant
              son enregistrement. */}
          <span className="info-value">
            {palier?.label ?? abo!.tier}
            {palier && abo!.interval
              ? ` — ${prixAffiche(palier, abo!.interval)}`
              : ''}
            {abo!.interval === 'yearly' ? ', facturé annuellement' : ''}
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">Colocations publiables</span>
          <span className="info-value">
            {abo!.publishedQuota === null ? 'Illimité' : abo!.publishedQuota}
          </span>
        </div>
        <div className="info-row">
          {/* Pendant l'essai, cette date n'est pas un renouvellement mais le
              PREMIER prélèvement : l'annoncer comme un renouvellement laissait
              croire à un abonnement déjà payé. `trialEnd` est nul dès que
              l'abonnement est payant, ou si Stripe n'a pas pu être relu — on
              retombe alors sur le libellé neutre. */}
          <span className="info-label">
            {resilie
              ? 'Fin de votre accès'
              : essai
                ? 'Fin de votre essai gratuit'
                : 'Prochain renouvellement'}
          </span>
          <span className="info-value">{formaterDate(abo!.currentPeriodEnd)}</span>
        </div>
      </div>

      {essai && !resilie && (
        <p className="subscription-trial">
          Votre premier prélèvement aura lieu le {formaterDate(abo!.trialEnd)},
          sauf résiliation d&apos;ici là.
        </p>
      )}

      <p className="subscription-manage-hint">
        Changer d&apos;offre ou de carte bancaire, télécharger vos factures :
      </p>
      <button className="subscription-cta" onClick={gerer} disabled={busy}>
        Gérer mon abonnement
      </button>

      {resilie ? (
        <>
          <p className="subscription-canceled">
            Résiliation programmée. Votre accès et votre site public restent en
            ligne jusqu&apos;au {formaterDate(abo!.currentPeriodEnd)}.
          </p>
          <button
            className="subscription-cta"
            onClick={() => agir('resume')}
            disabled={busy}
          >
            {busy ? '…' : 'Reprendre mon abonnement'}
          </button>
        </>
      ) : (
        <button
          className="subscription-cancel"
          onClick={() => setConfirmation(true)}
          disabled={busy}
        >
          Résilier mon abonnement
        </button>
      )}

      {erreur && <p className="subscription-error">{erreur}</p>}

      {confirmation && (
        <div className="subscription-modal-backdrop" role="dialog" aria-modal="true">
          <div className="subscription-modal">
            <h4>Résilier votre abonnement ?</h4>
            <p>
              Rien n&apos;est débité ni remboursé maintenant. Votre accès reste
              complet jusqu&apos;au{' '}
              <strong>{formaterDate(abo!.currentPeriodEnd)}</strong>, date à
              laquelle <strong>votre site public sera dépublié</strong>.
            </p>
            <p className="subscription-modal-note">
              Vos données sont conservées, et vous pouvez reprendre
              l&apos;abonnement à tout moment avant cette date.
            </p>
            <div className="subscription-modal-actions">
              <button
                className="subscription-modal-secondary"
                onClick={() => setConfirmation(false)}
                disabled={busy}
              >
                Annuler
              </button>
              <button
                className="subscription-cancel"
                onClick={() => agir('cancel')}
                disabled={busy}
              >
                {busy ? '…' : 'Confirmer la résiliation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSection;
